import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router";

import { appInstanceApi } from "@workspace/app-instance/api";
import { CheckOperation } from "@workspace/operations";
import { useProfile } from "@workspace/profile";
import { useTenant } from "@workspace/tenant-context";
import { Button } from "@workspace/ui/components/button";
import { ErrorComponent } from "@workspace/ui/components/error-component";
import { NotFoundComponent } from "@workspace/ui/components/not-found-component";
import { PlatformMqttBanner } from "@workspace/venue-management/service-health";

import { KioskBannerShell } from "@/components/kiosk-banner-shell";
import { PageLoadingFull } from "@/components/page-loading";

export const Route = createFileRoute("/_auth/_dashboard")({
	component: RouteComponent,
	pendingComponent: PageLoadingFull,
	errorComponent: ErrorComponent,
	notFoundComponent: NotFoundComponent,
	head: () => ({
		meta: [
			{
				title: "Staff Terminal",
			},
		],
	}),
});

/**
 * HA-746: the gate reads the profile and app instance live, from their providers (both sit above
 * RouterProvider), not from the router context. The router context is a snapshot taken when the
 * router last loaded, and it's only refreshed by `router.invalidate()`. The profile arrives from
 * its own query, and nothing invalidates when it does, so a `/profile` response that landed after
 * the load's last invalidate left `profile.user` undefined in the context for good. The page then
 * sat on "Loading app user..." until someone pressed Reload (7 of 50 full loads on main).
 */
function RouteComponent() {
	const profile = useProfile();
	const { appInstance, tenant } = useTenant();
	const queryClient = useQueryClient();

	// Reload refetches the query the gate is waiting on. `router.invalidate()` / `load()` refreshed
	// only the router context, which nothing here reads any more, so it no longer recovered anything.
	const reloadAppInstance = () => {
		if (!tenant) return;
		void queryClient.refetchQueries({
			queryKey: appInstanceApi.getOptionsCurrent(tenant).queryKey,
		});
	};

	// Was a beforeLoad redirect, which read the same stale context snapshot and so could miss it.
	if (appInstance?.status === "OnBoarding") {
		return <Navigate to="/login" />;
	}

	if (!appInstance) {
		return (
			<PageLoadingFull
				text={
					<div className="flex flex-col items-center justify-center">
						<p className="text-lg">Loading app instance...</p>
						<Button onClick={reloadAppInstance} className="mt-4">
							Reload
						</Button>
					</div>
				}
			/>
		);
	}

	if (!profile.user) {
		return (
			<PageLoadingFull
				text={
					<div className="flex flex-col items-center justify-center">
						<p className="text-lg">Loading app user...</p>
						<Button onClick={() => void profile.refetch()} className="mt-4">
							Reload
						</Button>
					</div>
				}
			/>
		);
	}

	return (
		<>
			<CheckOperation
				operation={"venue-management-staff-terminal"}
				operations={profile.operations}
			>
				{/* HA-737: floor staff print here, so they're told why printing is down. The shell keeps
				    the viewport-sized EPOS screens inside the screen while it shows (review M1). */}
				<KioskBannerShell
					banner={
						<PlatformMqttBanner
							tenant={tenant}
							operations={profile.operations}
							className="rounded-none border-x-0 border-t-0"
						/>
					}
				>
					<Outlet />
				</KioskBannerShell>
			</CheckOperation>
			<CheckOperation
				operation="venue-management-staff-terminal"
				operations={profile.operations}
				invert
			>
				No access. Please contact your administrator.
			</CheckOperation>
		</>
	);
}
